import { OktaAuth } from '@okta/okta-auth-js';
import fetch from '@zillow/fetchier';

var OKTA_DOMAIN = 'https://zillow.okta.com';
var ISSUER = OKTA_DOMAIN;
var CLIENT_ID = '0oa1kiqia2wn9GlO71d8';
var REDIRECT_URL = window.location.origin + "/user/acct/login/authorization-code/callback/"; // Response types
var EXTERNAL_AUTHENTICATION_ENDPOINT = '/user/account/services/ExternalAuthentication.htm';

var config = {
  issuer: ISSUER,
  // Required for login flow using getWithRedirect()
  clientId: CLIENT_ID,
  redirectUri: REDIRECT_URL,
  pkce: true,
  // Configure TokenManager to use memory instead of default localStorage
  storageManager: {
    token: {
      storageType: 'memory'
    }
  }
};
var oktaClient = new OktaAuth(config);
/**
 * Initiates Okta OIDC authentication.
 * First checks if token exists in memory:
 * (1) If a valid token exists, proceeds with Zillow account-service login using token.
 * (2) If valid token doesn't exist, checks if a current session exists.
 * (3) If a session exists, retrieves token from current session.
 * (4) If a session doesn't exist, starts the Okta authentication flow.
 * @param {Function} successHandler success handler post Okta authentication and account login
 * @param {Function} errorHandler  error handler
 */

function initiateOktaLogin(successHandler, errorHandler) {
  return new Promise(function ($return, $error) {
    var response, idToken, sessionExists, tokenFromSession;

    var $Try_1_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_1_Catch = function (err) {
      try {
        errorHandler(err);
        return $Try_1_Post();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    try {
      return Promise.resolve(oktaClient.tokenManager.get('idToken')).then(function ($await_5) {
        try {
          idToken = $await_5;

          if (idToken && !oktaClient.tokenManager.hasExpired(idToken)) {
            return Promise.resolve(handleZillowAccountLogin(idToken)).then(function ($await_6) {
              try {
                response = $await_6;
                successHandler(response);
                return $If_3.call(this);
              } catch ($boundEx) {
                return $Try_1_Catch($boundEx);
              }
            }.bind(this), $Try_1_Catch);
          } else {
            oktaClient.tokenManager.clear();
            return Promise.resolve(oktaClient.session.exists()).then(function ($await_7) {
              try {
                sessionExists = $await_7;

                if (sessionExists) {
                  return Promise.resolve(getTokenFromExistingSession()).then(function ($await_8) {
                    try {
                      tokenFromSession = $await_8;
                      return Promise.resolve(handleZillowAccountLogin(tokenFromSession)).then(function ($await_9) {
                        try {
                          response = $await_9;
                          successHandler(response);
                          return $If_4.call(this);
                        } catch ($boundEx) {
                          return $Try_1_Catch($boundEx);
                        }
                      }.bind(this), $Try_1_Catch);
                    } catch ($boundEx) {
                      return $Try_1_Catch($boundEx);
                    }
                  }.bind(this), $Try_1_Catch);
                } else {
                  return Promise.resolve(login()).then(function ($await_10) {
                    try {
                      return $If_4.call(this);
                    } catch ($boundEx) {
                      return $Try_1_Catch($boundEx);
                    }
                  }.bind(this), $Try_1_Catch);
                }

                function $If_4() {
                  return $If_3.call(this);
                }
              } catch ($boundEx) {
                return $Try_1_Catch($boundEx);
              }
            }.bind(this), $Try_1_Catch);
          }

          function $If_3() {
            return $Try_1_Post();
          }
        } catch ($boundEx) {
          return $Try_1_Catch($boundEx);
        }
      }.bind(this), $Try_1_Catch);
    } catch (err) {
      $Try_1_Catch(err);
    }
  });
}
/**
 * Callback that handles exchanging authorization code received from Okta server for tokens.
 * Note: parseFromUrl() will rewrite the url query params to maintain security of
 * authorization code, which may affect other query parameters in the url, if any exist
 * @param {Function} successHandler
 * @param {Function} errorHandler
 */

function handleOktaLoginRedirect(successHandler, errorHandler) {
  return new Promise(function ($return, $error) {
    var response, idToken, loginResponse;

    var $Try_2_Post = function () {
      try {
        return $return();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    var $Try_2_Catch = function (err) {
      try {
        errorHandler(err);
        return $Try_2_Post();
      } catch ($boundEx) {
        return $error($boundEx);
      }
    };

    try {
      return Promise.resolve(oktaClient.token.parseFromUrl()).then(function ($await_11) {
        try {
          response = $await_11;
          oktaClient.tokenManager.setTokens(response.tokens);
          return Promise.resolve(oktaClient.tokenManager.get('idToken')).then(function ($await_12) {
            try {
              idToken = $await_12;
              return Promise.resolve(handleZillowAccountLogin(idToken)).then(function ($await_13) {
                try {
                  loginResponse = $await_13;
                  successHandler(loginResponse);
                  return $Try_2_Post();
                } catch ($boundEx) {
                  return $Try_2_Catch($boundEx);
                }
              }, $Try_2_Catch);
            } catch ($boundEx) {
              return $Try_2_Catch($boundEx);
            }
          }, $Try_2_Catch);
        } catch ($boundEx) {
          return $Try_2_Catch($boundEx);
        }
      }, $Try_2_Catch);
    } catch (err) {
      $Try_2_Catch(err);
    }
  });
}
/**
 * Okta authentication to get tokens. This function will result in an authorization code
 * that is sent as parameters to the configured redirectUri,
 * in the search query portion of the URL by default.
 */

function login() {
  return new Promise(function ($return, $error) {
    oktaClient.token.getWithRedirect({
      scopes: ['openid', 'email']
    });
    return $return();
  });
}
/**
 * Get Okta token without prompting user to authenticate.
 * Used only when a session already exists
 * @returns ID token
 */


function getTokenFromExistingSession() {
  return new Promise(function ($return, $error) {
    var response;
    return Promise.resolve(oktaClient.token.getWithoutPrompt({
      scopes: ['openid', 'email']
    })).then(function ($await_14) {
      try {
        response = $await_14;
        oktaClient.tokenManager.setTokens(response.tokens);
        return $return(oktaClient.tokenManager.get('idToken'));
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }, $error);
  });
}
/**
 * Log in Zillow account. Calls ExternalAuthentication.htm
 * @param {import('@okta/okta-auth-js').IDToken} token IDToken object containing idToken string
 * @returns Account login response
 */


function handleZillowAccountLogin(token) {
  return new Promise(function ($return, $error) {
    var authURL, response;
    authURL = EXTERNAL_AUTHENTICATION_ENDPOINT + "?at=okta&idToken=" + token.idToken;
    return Promise.resolve(fetch(authURL, {
      method: 'POST',
      credentials: 'same-origin'
    })).then(function ($await_15) {
      try {
        response = $await_15;

        if (!response.ok) {
          return $error(new Error('Unable to log in to account'));
        }

        return $return(response.json());
      } catch ($boundEx) {
        return $error($boundEx);
      }
    }, $error);
  });
}

export { handleOktaLoginRedirect, initiateOktaLogin };
